import { faEnvelope, faLink } from '@fortawesome/pro-light-svg-icons'

import { Button, SegmentedControl, SidebarModal } from '@domain-group/suit-theme'

import useBulkShareArtworks from '@hooks/useBulkShareArtworks'

import type { CampaignModel } from '@models/types'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ArtworkList from './components/ArtworkList'
import EmailDetails from './components/EmailDetails'
import PublicLink from './components/PublicLink'
import UserList from './components/UserList'

const MODAL_KEY = 'BulkShareArtworksModal'

type ModalCallbacks = {
  closeModal: () => void,
  bulkShareArtworks: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  campaign: CampaignModel,
  showModal: boolean,
}

const BulkShareArtworksModal = (props: ModalProps) => {
  const { callbacks, campaign, showModal } = props
  const { bulkShareArtworks, closeModal } = callbacks || {}

  const bulkShareArtworksPayload = useBulkShareArtworks({ campaign })
  const {
    callbacks: {
      bulkShareArtwork: bulkShareFn,
      setShareMode,
    },
    canShareViaEmail,
    selectedArtworkIds,
    selectedUsers,
    shareMode,
  } = bulkShareArtworksPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Share"
        titleSecondLine="Artworks"
      />

      <SidebarModal.Content hasFooter={shareMode === 'email'}>
        <SegmentedControl
          activeButtonKey={shareMode}
          boxProps={{ marginBottom: 'large' }}
          buttons={[
            // @ts-expect-error TS2322
            { key: 'email', icon: faEnvelope, label: 'Email' },
            // @ts-expect-error TS2322
            { key: 'link', icon: faLink, label: 'Links' },
          ]}
          // @ts-expect-error TS2322
          onClick={(key: 'email' | 'link') => setShareMode(key)}
        />

        <ArtworkList bulkShareArtworksPayload={bulkShareArtworksPayload} />

        {shareMode === 'email' && !!selectedArtworkIds.length && (
          <EmailDetails bulkShareArtworksPayload={bulkShareArtworksPayload} />
        )}

        {shareMode === 'link' && !!selectedArtworkIds.length && (
          <>
            <UserList bulkShareArtworksPayload={bulkShareArtworksPayload} />
            <PublicLink bulkShareArtworksPayload={bulkShareArtworksPayload} />
          </>
        )}
      </SidebarModal.Content>

      {shareMode === 'email' && (
        <SidebarModal.Footer>
          <Button
            buttonStyle="primaryCreate"
            disabled={!canShareViaEmail}
            onClick={() => bulkShareArtworks({
              callbacks: {
                action: bulkShareFn,
                afterAction: closeModal,
              },
              toastText: 'Artwork Shared',
            })}
            size="large"
          >
            {selectedUsers.length === 1 ? 'Send Message' : 'Send Messages'}
          </Button>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkShareArtworksModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
